import React, { useState, useEffect, useMemo, useRef } from "react";

import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Calendar,
  Heart,
  Sun,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import dayjs, { Dayjs } from "dayjs";

import { useGetCurrentYearHolidaysQuery, type IHoliday } from "../../store/apis/holidayManagement.api";
import {
  useGetMyLeavesQuery,
  useCreateLeaveMutation,
  usePullbackLeaveMutation,
  useGetMyLeaveBalanceQuery,
} from "../../store/apis/leave.api";
import type { ILeaveBalanceUI } from "../../types/leave.api.types";
import { useAuth } from "../../store/hooks/useAuth";
import {
  ConfirmationModal,
  Select,
  DatePicker,
  PageHeader,
  Button,
  SimpleTooltip,
} from "../common";
import Badge from "../common/Badge";
import Modal2, { Modal2Footer, Modal2Button } from "../common/Modal2";
import LeaveTableSection from "./components/LeaveTableSection";
import { getWorkflowStatusVariant } from "../../utils/badgeVariants";
import { calculateLeaveDays, checkDateSpansSunday } from "../../utils/leaveUtils";
import {
  getWorkflowStatusColor,
  getWorkflowStatusFallbackLabel,
  isPendingWorkflowStatus,
  WORKFLOW_APPROVED_STATUSES,
  WORKFLOW_REJECTED_STATUSES,
  WORKFLOW_CANCELLED_STATUSES,
  WorkflowStatusCode,
  LeaveTypesEnum,
} from "../../utils/constants";
import { ILeaveRequest as LeaveRequestType } from "../../types/leave.types";
import { LeaveTableSectionSkeleton } from "./Skeleton";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { TextArea } from "../common/TextArea";
import type { ApiError } from "../../store/utils/apiError";
import type { ILeave } from "../../types/leave.api.types";

type TabFilter = "all" | "pending" | "approved" | "rejected";

type LeaveRequest = LeaveRequestType;

interface LeaveBalance {
  [key: string]: number;
}

interface Holiday {
  id?: string;
  name: string;
  date: string;
  year: number;
  isMandatory: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

const DEFAULT_PAGE_SIZE = 10;

const MyLeaves: React.FC = () => {
  const { user } = useAuth();
  const [createLeave, { isLoading: creating }] = useCreateLeaveMutation();
  const [pullbackLeave, { isLoading: pullingBack }] =
    usePullbackLeaveMutation();

  const canManage = useHasPermission(PERMISSIONS.APPLY_LEAVE_MANAGE);

  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance>({});

  const [availableLeaveType, setAvailableLeaveType] = useState<
    ILeaveBalanceUI["types"]
  >([]);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const [confirmTarget, setConfirmTarget] = useState<LeaveRequest | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(
    null,
  );
  const [optionalLeaves, setOptionalLeaves] = useState<Holiday[]>([]); // optional leaves
  const [optionalLeave, setOptionalLeave] = useState<string>(""); //selected optional leave

  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    itemsPerPage: DEFAULT_PAGE_SIZE,
    totalItems: 0,
  });

  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabFilter>("all");
  const chipScrollRef = useRef<HTMLDivElement>(null);
  const [chipScrollable, setChipScrollable] = useState(false);

  useEffect(() => {
    const el = chipScrollRef.current;
    if (!el) return;
    const check = () => setChipScrollable(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [availableLeaveType]);

  const CARD_ICONS = [
    { icon: <Calendar className="w-5 h-5 text-[var(--color-primary-600)]" />, bg: "var(--color-primary-100)" },
    { icon: <Heart className="w-5 h-5 text-[var(--color-error-dark)]" />, bg: "var(--color-error-light)" },
    { icon: <Sun className="w-5 h-5 text-[var(--color-warning-dark)]" />, bg: "var(--color-warning-light)" },
    { icon: <RefreshCw className="w-5 h-5 text-[var(--color-info)]" />, bg: "var(--color-info-light)" },
  ];

  const statCards = useMemo(() => {
    return [...availableLeaveType]
      .sort((a, b) => b.credited - a.credited)
      .slice(0, 4);
  }, [availableLeaveType]);

  const {
    data: leavePayload,
    isLoading,
    isFetching,
  } = useGetMyLeavesQuery(
    {
      page: paginationState.currentPage,
      limit: paginationState.itemsPerPage,
    },
    {
      skip: !user?.id,
    },
  );

  const { data: balancePayload, isLoading: isBalanceLoading } = useGetMyLeaveBalanceQuery(
    {},
    {
      skip: !user?.id,
    },
  );

  // Added this useEffect to populate available leave types and balances
  useEffect(() => {
    if (!balancePayload?.types) return;
    const balances: LeaveBalance = {};

    balancePayload.types.forEach((t) => {
      balances[t.code] = t.available;
    });

    setLeaveBalances(balances);
    setAvailableLeaveType(balancePayload.types);
  }, [balancePayload]);

  const leaveRequests: LeaveRequest[] =
    leavePayload?.data?.map((r: ILeave) => ({
      ...r,
      id: r.id,
      leaveType: r.leaveType as LeaveRequest['leaveType'],
      reason: r.reason ?? '',
      status: r.status as LeaveRequest['status'],
      isHalfDay: r.isHalfDay ?? false,
      appliedDate: r.createdAt,
    })) ?? [];

  const filteredRequests = useMemo(() => {
    if (activeTab === "all") return leaveRequests;
    return leaveRequests.filter((r) => {
      const s = r.status || "";
      switch (activeTab) {
        case "pending":
          return isPendingWorkflowStatus(s);
        case "approved":
          return WORKFLOW_APPROVED_STATUSES.includes(s as WorkflowStatusCode);
        case "rejected":
          return WORKFLOW_REJECTED_STATUSES.includes(s as WorkflowStatusCode);
        default:
          return true;
      }
    });
  }, [activeTab, leaveRequests]);

  // Form state
  const [formData, setFormData] = useState({
    leaveType: "",
    leaveTypeCode: "",
    startDate: "",
    endDate: "",
    reason: "",
    isHalfDay: false,
    halfDayType: "first_half", //for now we will put it  default to first half, as there is no UI to select half day type
  });

  const showErrorToast = (message: string) => {
    toast.error(message);
  };

  const showSuccessToast = (message: string) => {
    toast.success(message);
  };

  useEffect(() => {
    if (leavePayload?.total) {
      setPaginationState((prev) => ({
        ...prev,
        totalItems: leavePayload.total,
      }));
    }
  }, [leavePayload]);

  const { data: holidaysData } = useGetCurrentYearHolidaysQuery();

  useEffect(() => {
    // getCurrentYearHolidays returns the array directly (transformResponse unwraps backend { data } and returns the list)
    const list: IHoliday[] = Array.isArray(holidaysData) ? holidaysData : [];
    const mapped: Holiday[] = list.map((h) => ({
      id: h.id,
      name: h.name,
      date: h.date,
      year: h.year,
      isMandatory: h.isMandatory,
      description: h.description,
      createdAt: h.createdAt,
      updatedAt: h.updatedAt,
    }));
    setOptionalLeaves(mapped.filter((leave) => !leave.isMandatory));
  }, [holidaysData]);

  const handleHistoryPageChange = (page: number) => {
    setPaginationState((prev) => ({ ...prev, currentPage: page }));
  };

  const handleHistoryItemsPerPageChange = (itemsPerPage: number) => {
    setPaginationState((prev) => ({
      ...prev,
      itemsPerPage,
      currentPage: 1,
    }));
  };

  const canPullback = (req: LeaveRequest): boolean => {
    if (!isPendingWorkflowStatus(req.status)) return false;
    const todayStr = new Date().toISOString().split("T")[0];
    return req.startDate > todayStr;
  };
  const onPullbackClick = (req: LeaveRequest) => {
    setConfirmTarget(req);
    setConfirmOpen(true);
  };

  const confirmPullback = async () => {
    if (!user?.id || !confirmTarget) return;
    try {
      setConfirmLoading(true);
      await pullbackLeave({
        id: confirmTarget.id,
        body: {},
      }).unwrap();
      showSuccessToast("Leave pulled back successfully.");
    } catch (error: unknown) {
      const err = error as ApiError;
      const rawMessage = err?.data?.message || err?.message || "Failed to pull back leave.";
      const errorMessage = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;
      showErrorToast(errorMessage);
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  };

  const handleViewDetails = (request: LeaveRequest) => {
    setSelectedRequest(request);
    setDetailsModalOpen(true);
  };

  const handleLeaveTypeChange = (
    value: string | number | (string | number)[],
  ) => {
    if (Array.isArray(value)) return;
    const selected = availableLeaveType.find((t) => t.id === value);
    setFormData((prev) => {
      const newData = {
        ...prev,
        leaveType: value as string,
        leaveTypeCode: selected?.code || "",
        // Reset isHalfDay if switching to optional_holiday as it doesn't support half days
        isHalfDay:
          value === LeaveTypesEnum.OPTIONAL_HOLIDAY ? false : prev.isHalfDay,
      };

      // Clear dates if leave type changes to flexi_weekend and dates are not Saturdays
      if (value === "flexi_weekend") {
        if (newData.startDate) {
          const startDate = new Date(newData.startDate);
          if (startDate.getDay() !== 6) {
            // 6 = Saturday
            newData.startDate = "";
          }
        }
        if (newData.endDate) {
          const endDate = new Date(newData.endDate);
          if (endDate.getDay() !== 6) {
            // 6 = Saturday
            newData.endDate = "";
          }
        }
      }

      return newData;
    });
  };

  const handleStartDateChange = (value: Dayjs | null) => {
    const newStartDate = value ? value.format("YYYY-MM-DD") : "";

    if (value && value.day() === 0) {
      showErrorToast(
        "Leave cannot start on Sunday. Please select another start date.",
      );
      return;
    }

    setFormData((prev) => {
      if (newStartDate && prev.endDate) {
        const start = new Date(newStartDate);
        const end = new Date(prev.endDate);
        if (start > end) {
          showErrorToast("Start date cannot be after end date.");
          return {
            ...prev,
            startDate: newStartDate,
            endDate: "",
          };
        }
      }

      return {
        ...prev,
        startDate: newStartDate,
      };
    });
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    const newEndDate = value ? value.format("YYYY-MM-DD") : "";

    setFormData((prev) => {
      if (newEndDate && prev.startDate) {
        const start = new Date(prev.startDate);
        const end = new Date(newEndDate);
        if (end < start) {
          showErrorToast(
            "End date must be greater than or equal to start date.",
          );
          return prev;
        }
      }

      return {
        ...prev,
        endDate: newEndDate,
      };
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const calculateDays = (
    startDate: string,
    endDate: string,
    isHalfDay: boolean,
  ): number => calculateLeaveDays(startDate, endDate, isHalfDay);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.id) {
      showErrorToast("User not found. Please try again.");
      return;
    }
    if (!formData.leaveType) {
      showErrorToast("Please select a leave type.");
      return;
    }

    if (formData.startDate) {
      const startDay = new Date(formData.startDate).getDay();
      if (startDay === 0) {
        showErrorToast("Cannot make a leave request on Sunday.");
        return;
      }
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      const spansSunday = checkDateSpansSunday(formData.startDate, formData.endDate);

      if (spansSunday) {
        showErrorToast("You cannot include Sunday(s) in the Leave Duration.");
        return;
      }

      if (end < start) {
        showErrorToast("End date must be greater than or equal to start date.");
        return;
      }
    }

    const requestedDays = calculateDays(
      formData.startDate,
      formData.endDate,
      formData.isHalfDay,
    );

    if (requestedDays <= 0) {
      showErrorToast("Please select valid start and end dates.");
      return;
    }

    if (formData.reason.trim() === "") {
      showErrorToast("Please enter a reason for the leave request.");
      return;
    }

    if (formData.leaveType === "flexi_weekend") {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);

      const startDay = startDate.getDay();
      const endDay = endDate.getDay();

      if (startDay !== 6) {
        showErrorToast(
          "Flexi Weekend Leave can only be applied for Saturdays. Please select a Saturday for the start date.",
        );
        return;
      }

      if (endDay !== 6) {
        showErrorToast(
          "Flexi Weekend Leave can only be applied for Saturdays. Please select a Saturday for the end date.",
        );
        return;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);
      const nonSaturdayDates: string[] = [];

      for (
        let d = new Date(start.getTime());
        d <= end;
        d.setDate(d.getDate() + 1)
      ) {
        if (d.getDay() !== 6) {
          nonSaturdayDates.push(d.toISOString().split("T")[0]);
        }
      }

      if (nonSaturdayDates.length > 0) {
        showErrorToast(
          `Flexi Weekend Leave can only be applied for Saturdays. The following dates in your range are not Saturdays: ${nonSaturdayDates.join(
            ", ",
          )}`,
        );
        return;
      }
    }

    if (
      formData.leaveTypeCode !== "lwp" &&
      formData.leaveTypeCode !== "wfh" &&
      formData.leaveTypeCode !== "optional_holiday"
    ) {
      const availableBalance = leaveBalances[formData.leaveTypeCode] || 0;

      if (requestedDays > availableBalance) {
        showErrorToast(
          `Insufficient leave balance. You have ${availableBalance} days available.`,
        );
        return;
      }
    }
    try {
      const leaveData = {
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        days: requestedDays,
        reason: formData.reason,
        isHalfDay: formData.isHalfDay,

        ...(formData.isHalfDay && {
          halfDayType: formData.halfDayType as 'first_half' | 'second_half',
        }),
      };
      await createLeave(leaveData).unwrap();

      setFormData({
        leaveTypeCode: "",
        leaveType: "",
        startDate: "",
        endDate: "",
        reason: "",
        isHalfDay: false,
        halfDayType: "first_half",
      });

      showSuccessToast(
        `Leave request submitted successfully! ${requestedDays} day(s).`,
      );
    } catch (error: unknown) {
      const err = error as ApiError;
      console.error("Error submitting leave request:", err);
      const rawMessage = err?.data?.message || err?.message || "Failed to submit leave request. Please try again.";
      const errorMessage = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;
      showErrorToast(errorMessage);
    }
  };

  const getStatusIcon = (status: string) => {
    const statusCode = status as WorkflowStatusCode;
    if (isPendingWorkflowStatus(status)) {
      return <Clock className="w-4 h-4 text-[var(--color-warning)]" />;
    }
    if (WORKFLOW_APPROVED_STATUSES.includes(statusCode)) {
      return <CheckCircle className="w-4 h-4 text-[var(--color-success)]" />;
    }
    if (WORKFLOW_REJECTED_STATUSES.includes(statusCode)) {
      return <XCircle className="w-4 h-4 text-[var(--color-error)]" />;
    }
    if (WORKFLOW_CANCELLED_STATUSES.includes(statusCode)) {
      return <AlertCircle className="w-4 h-4 text-[var(--color-text-secondary)]" />;
    }
    return <Clock className="w-4 h-4 text-[var(--color-text-tertiary)]" />;
  };

  const handleOptionalLeaveChange = (value: string) => {
    setFormData({
      ...formData,
      startDate:
        optionalLeaves.find((leave) => leave.id === value)?.date || "",
      endDate: optionalLeaves.find((leave) => leave.id === value)?.date || "",
      isHalfDay: false,
    });
  };

  const getStatusColor = (status: string) => getWorkflowStatusColor(status);


  const getStatusLabel = (request: LeaveRequest) =>
    request.statusLabel ||
    getWorkflowStatusFallbackLabel(request.status, request.currentActorName);
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "—"; // Return dash for missing dates

    const dateOnly = dateString.split("T")[0];
    const [year, month, day] = dateOnly
      .split("-")
      .map((num) => parseInt(num, 10));
    const localDate = new Date(year, month - 1, day);

    return localDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="w-full">

      <PageHeader
        title="My Leaves"
        description="Track your balance and request time off."
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "My Leaves" }]}
        actions={canManage ? (
          <Button
            appearance="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setApplyModalOpen(true)}
          >
            Apply Leave
          </Button>
        ) : undefined}
      />

      {/* ── Balance Stat Cards ── */}
      {isBalanceLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-5 sm:mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-[154px] rounded-[16px] bg-[var(--color-bg-skeleton)] animate-pulse"
            />
          ))}
        </div>
      ) : statCards.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-5 sm:mb-6">
          {statCards.map((type, index) => {
            const cfg = CARD_ICONS[index];
            const pct = type.credited > 0 ? (type.available / type.credited) * 100 : 0;
            return (
              <div
                key={type.code}
                className="rounded-[16px] bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] p-[22px] transition-all hover:-translate-y-[3px] hover:shadow-[var(--shadow-md)]"
              >
                <div className="flex items-center gap-3 mb-4 min-w-0">
                  <div
                    className="w-11 h-11 rounded-[12px] flex items-center justify-center flex-shrink-0"
                    style={{ background: cfg.bg }}
                  >
                    {cfg.icon}
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <SimpleTooltip label={type.label} side="top" className="min-w-0 w-full block">
                      <p className="text-sm font-bold text-[var(--color-text-primary)] truncate block w-full">
                        {type.label}
                      </p>
                    </SimpleTooltip>
                  </div>
                </div>
                <div className="mb-2">
                  <span className="text-[24px] font-extrabold text-[var(--color-text-primary)]">
                    {type.available}
                    <span className="text-sm font-semibold text-[var(--color-text-tertiary)]">/{type.credited}</span>
                  </span>
                </div>
                <div className="h-[6px] rounded-[999px] bg-[var(--color-bg-muted)] overflow-hidden mb-4">
                  <div
                    className="h-full rounded-[999px] bg-[var(--color-primary-600)] transition-all"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
                <p className="text-[11.5px] font-semibold text-[var(--color-text-tertiary)]">
                  {type.used} day{type.used === 1 ? "" : "s"} used this year
                </p>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* ── Leave History ── */}
      {isLoading ? (
        <LeaveTableSectionSkeleton />
      ) : (
        <LeaveTableSection
          requests={filteredRequests}
          loading={isFetching}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onViewDetails={handleViewDetails}
          onPullback={canManage ? onPullbackClick : () => { }}
          canPullback={canManage ? canPullback : () => false}
          getStatusLabel={getStatusLabel}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          formatDate={formatDate}
          pagination={{
            currentPage: paginationState.currentPage,
            itemsPerPage: paginationState.itemsPerPage,
            totalItems: paginationState.totalItems,
            onPageChange: handleHistoryPageChange,
            onItemsPerPageChange: handleHistoryItemsPerPageChange,
          }}
        />
      )}

      {/* ── Apply Leave Modal ── */}
      <Modal2
        isOpen={applyModalOpen}
        onClose={() => {
          setApplyModalOpen(false);
          setFormData({
            leaveTypeCode: "",
            leaveType: "",
            startDate: "",
            endDate: "",
            reason: "",
            isHalfDay: false,
            halfDayType: "first_half",
          });
        }}
        title="Apply for Leave"
        size="md"
      >
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">
          Request time off for yourself.
        </p>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Leave type chips (horizontal scroll) */}
          <div className="relative">
            <div
              className="absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-[var(--color-bg-base)] to-transparent pointer-events-none z-10"
              style={{ opacity: chipScrollable ? 1 : 0 }}
            />
            {chipScrollable && (
              <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-[var(--color-bg-base)] to-transparent pointer-events-none z-10" />
            )}
            <div
              ref={chipScrollRef}
              className="flex gap-[9px] overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              {availableLeaveType.map((type) => {
                const isActive = formData.leaveType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleLeaveTypeChange(type.id)}
                    className={`h-[40px] px-4 rounded-[11px] text-xs font-bold cursor-pointer transition-all flex-shrink-0 focus-visible:ring-2 focus-visible:ring-[var(--color-primary-600)] focus-visible:outline-none ${isActive
                      ? "border border-[var(--color-primary-600)] bg-[var(--color-primary-50)] text-[var(--color-primary-600)]"
                      : "border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] text-[var(--color-text-strong)] hover:border-[var(--color-primary-300)]"
                      }`}
                  >
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Optional holiday select */}
          {formData.leaveTypeCode === "optional_holiday" && (
            <Select
              label="Optional Leaves"
              required
              value={optionalLeave}
              onChange={(value) => {
                setOptionalLeave(value as string);
                handleOptionalLeaveChange(value as string);
              }}
              options={optionalLeaves.map((type) => ({
                value: type.id || "",
                label: `${type.name} - ${dayjs(type.date).format("DD/MM/YYYY")}`,
              }))}
              placeholder="Select optional leave"
              searchable={false}
            />
          )}

          {/* Date pickers */}
          {formData.leaveType !== LeaveTypesEnum.OPTIONAL_HOLIDAY && (
            <div className="grid grid-cols-2 gap-4">
              <DatePicker
                label="Start Date"
                required
                value={formData.startDate ? dayjs(formData.startDate) : null}
                onChange={handleStartDateChange}
                placeholder="Select start date"
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDates={(date) => date.day() === 0}
              />
              <DatePicker
                label="End Date"
                required
                value={formData.endDate ? dayjs(formData.endDate) : null}
                onChange={handleEndDateChange}
                placeholder="Select end date"
                format="DD/MM/YYYY"
                allowClear={false}
                minDate={formData.startDate ? dayjs(formData.startDate) : undefined}
                disabledDates={(date) => {
                  if (formData.startDate) {
                    return date.isBefore(dayjs(formData.startDate), "day");
                  }
                  return false;
                }}
              />
            </div>
          )}

          {/* Flexi weekend errors */}
          {formData.leaveType === "flexi_weekend" && formData.startDate && new Date(formData.startDate).getDay() !== 6 && (
            <div className="flex items-center gap-2 p-3 bg-[var(--color-error-light)] border border-[var(--color-error)]/20 rounded-[11px] text-xs text-[var(--color-error-dark)]">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>Flexi Weekend Leave can only be applied for Saturdays.</span>
            </div>
          )}
          {formData.leaveType === "flexi_weekend" && formData.endDate && new Date(formData.endDate).getDay() !== 6 && (
            <div className="flex items-center gap-2 p-3 bg-[var(--color-error-light)] border border-[var(--color-error)]/20 rounded-[11px] text-xs text-[var(--color-error-dark)]">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>Flexi Weekend Leave can only be applied for Saturdays.</span>
            </div>
          )}

          {/* Half day checkbox & selection */}
          {formData.leaveType !== LeaveTypesEnum.OPTIONAL_HOLIDAY && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  id="isHalfDay"
                  type="checkbox"
                  name="isHalfDay"
                  checked={formData.isHalfDay}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded border-[var(--color-border-primary)] text-[var(--color-primary-600)] focus:ring-[var(--color-primary-600)] cursor-pointer"
                />
                <label htmlFor="isHalfDay" className="text-xs font-medium text-[var(--color-text-primary)] cursor-pointer">
                  Apply for half day
                </label>
              </div>

              {formData.isHalfDay && (
                <div className="flex items-center gap-6 pl-6 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-[var(--color-text-primary)]">
                    <input
                      type="radio"
                      name="halfDayType"
                      value="first_half"
                      checked={formData.halfDayType === "first_half"}
                      onChange={() => setFormData((prev) => ({ ...prev, halfDayType: "first_half" }))}
                      className="w-3.5 h-3.5 text-[var(--color-primary-600)] border-[var(--color-border-primary)] focus:ring-[var(--color-primary-600)]"
                    />
                    First Half
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-[var(--color-text-primary)]">
                    <input
                      type="radio"
                      name="halfDayType"
                      value="second_half"
                      checked={formData.halfDayType === "second_half"}
                      onChange={() => setFormData((prev) => ({ ...prev, halfDayType: "second_half" }))}
                      className="w-3.5 h-3.5 text-[var(--color-primary-600)] border-[var(--color-border-primary)] focus:ring-[var(--color-primary-600)]"
                    />
                    Second Half
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <TextArea
            label="Reason"
            value={formData.reason ?? ""}
            onChange={(value: string) => setFormData({ ...formData, reason: value })}
            placeholder="Please provide a reason for your leave request..."
            required
          />

          <Modal2Footer>
            <Modal2Button
              variant="secondary"
              onClick={() => {
                setApplyModalOpen(false);
                setFormData({
                  leaveTypeCode: "",
                  leaveType: "",
                  startDate: "",
                  endDate: "",
                  reason: "",
                  isHalfDay: false,
                  halfDayType: "first_half",
                });
              }}
              disabled={creating}
            >
              Cancel
            </Modal2Button>
            <Modal2Button
              variant="primary"
              type="submit"
              loading={creating}
              disabled={creating}
            >
              {creating ? "Submitting..." : "Submit Request"}
            </Modal2Button>
          </Modal2Footer>
        </form>
      </Modal2>

      {/* ── Details Modal ── */}
      <Modal2
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        title="Leave Details"
        size="md"
        footer={
          <Modal2Footer justify="end">
            <Modal2Button variant="secondary" onClick={() => setDetailsModalOpen(false)}>
              Close
            </Modal2Button>
          </Modal2Footer>
        }
      >
        {selectedRequest && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Leave Type
                </p>
                <p className="font-semibold text-[var(--color-text-primary)]">
                  {selectedRequest.leaveTypeName}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Period
                </p>
                <p className="font-semibold text-[var(--color-text-primary)]">
                  {formatDate(selectedRequest.startDate)} - {formatDate(selectedRequest.endDate)}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Applied On
                </p>
                <p className="font-semibold text-[var(--color-text-primary)]">
                  {formatDate(selectedRequest.appliedDate)}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Status
                </p>
                <Badge
                  variant={getWorkflowStatusVariant(selectedRequest.status)}
                  size="middle"
                  icon={getStatusIcon(selectedRequest.status)}
                >
                  {getStatusLabel(selectedRequest)}
                </Badge>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Days
                </p>
                <p className="font-semibold text-[var(--color-text-primary)]">
                  {selectedRequest.days}{" "}
                  {selectedRequest.isHalfDay
                    ? selectedRequest.halfDayType === "second_half"
                      ? "second-half"
                      : "first-half"
                    : "full"}{" "}
                  day{selectedRequest.days !== 1 ? "s" : ""}
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-1">
                  Reason
                </p>
                <p className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap break-words">
                  {selectedRequest.reason}
                </p>
              </div>
            </div>

            {/* Approval trail */}
            {selectedRequest.decisions && selectedRequest.decisions.length > 0 && (
              <div className="border-t border-[var(--color-border-primary)] pt-4">
                <p className="text-xs font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] mb-3">
                  Approval Trail
                </p>
                <div className="space-y-3">
                  {selectedRequest.decisions.map((decision: { stepName?: string; actorName?: string; status?: string }, idx: number) => {
                    const dStatus = decision.status || "";
                    const isDone = WORKFLOW_APPROVED_STATUSES.includes(dStatus as WorkflowStatusCode);
                    const isRejected = WORKFLOW_REJECTED_STATUSES.includes(dStatus as WorkflowStatusCode);
                    const isCurrent = isPendingWorkflowStatus(dStatus);
                    return (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isDone
                              ? "bg-[var(--color-success-light)] text-[var(--color-success)]"
                              : isRejected
                                ? "bg-[var(--color-error-light)] text-[var(--color-error)]"
                                : isCurrent
                                  ? "bg-[var(--color-primary-50)] text-[var(--color-primary-600)]"
                                  : "bg-[var(--color-bg-muted)] text-[var(--color-text-tertiary)]"
                              }`}
                          >
                            {isDone ? (
                              <CheckCircle className="w-3.5 h-3.5" />
                            ) : isRejected ? (
                              <XCircle className="w-3.5 h-3.5" />
                            ) : (
                              <div className={`w-2 h-2 rounded-full ${isCurrent ? "bg-[var(--color-primary-600)]" : "bg-[var(--color-text-tertiary)]"
                                }`} />
                            )}
                          </div>
                          {idx < (selectedRequest.decisions?.length || 0) - 1 && (
                            <div className="w-px h-full min-h-[16px] bg-[var(--color-border-primary)]" />
                          )}
                        </div>
                        <div className="pb-3">
                          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                            {decision.stepName || "Decision"}
                          </p>
                          {decision.actorName && (
                            <p className="text-xs text-[var(--color-text-tertiary)]">
                              by {decision.actorName}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedRequest.rejectionReason && (
              <div className="p-3 bg-[var(--color-error-light)] border border-[var(--color-error)]/20 rounded-[11px]">
                <p className="font-semibold text-xs text-[var(--color-error-dark)]">Rejection Reason</p>
                <p className="text-sm text-[var(--color-error-dark)] mt-1">{selectedRequest.rejectionReason}</p>
              </div>
            )}
          </div>
        )}
      </Modal2>

      {/* ── Confirmation Modal (pullback) ── */}
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmPullback}
        title="Pull back leave request"
        message={
          confirmTarget
            ? `Are you sure you want to pull back your ${confirmTarget.leaveTypeName}
              for ${formatDate(confirmTarget.startDate)} - ${formatDate(confirmTarget.endDate)}?`
            : ""
        }
        type="warning"
        confirmText={confirmLoading ? "Pulling back..." : "Pull back"}
        isLoading={confirmLoading}
      />
    </div>
  );
};

export default MyLeaves;
